// Wells.js
// This will contain the Wells Class for the sampling events page, basically holding all the information about the wells that comes from the SamplingWells table and the data from
// It will also contain the Field Measurements Class that is a list of data that forms a table for each well sampling sheet.
// It will also contain a section for the Sampling Employees Signature Section.
import { v4 as uuidv4 } from "uuid";

export class SamplingEmployeesSignature {
  constructor(data = {}) {
    this.rowId = data.id || uuidv4();
    this.samplingEventWellId = data.sampling_event_well_id || "";
    this.employeeId = data.employee_id || "";
    this.employeeName = data.employee_name || "";
    this.signature = data.signature
      ? "data:image/png;base64," + data.signature
      : "";
  }

  // Updating the employee information, when someone is selected from the employee dropdown list
  setSelectedEmployee(employee) {
    if (!employee) {
      this.employeeId = "";
      this.employeeName = "";
    } else {
      this.employeeId = employee.employeeId;
      this.employeeName = employee.employeeName;
    }
  }

  // updating the signature
  setSignature(signature) {
    if (!signature) {
      this.signature = "";
    } else {
      if (signature.startsWith("data:image/png;base64,")) {
        this.signature = signature;
      } else {
        this.signature = "data:image/png;base64," + signature;
      }
    }
  }

  toPatch(samplingEventId, action) {
    return {
      patch_type: "sampling_event_sampling_employee_signatures",
      action: action,
      id: this.rowId,
      sampling_event_well_id: this.samplingEventWellId,
      sampling_event_id: samplingEventId,
      employee_id: this.employeeId,
      signature: this.signature,
    };
  }

  // To json for sending to the database
  toJson() {
    // Removing the data:image/png;base64, from the signature
    const signature = this.signature
      .replace("data:image/png;base64,", "")
      .trim();
    return {
      id: this.rowId,
      sampling_event_well_id: this.samplingEventWellId,
      employee_id: this.employeeId,
      employee_name: this.employeeName,
      signature: signature,
    };
  }
}

export class FieldMeasurements {
  constructor(data = {}) {
    this.rowId = data.id || uuidv4();
    this.sortOrder = data.sort_order ?? null;
    this.wellId = data.well_id || "";
    this.measurementDate = data.measurement_date || "";
    this.measurementTime = data.measurement_time || "";
    this.extractedVolume = data.extracted_volume || null;
    this.extractedVolumeUnit = data.extracted_volume_unit || null;
    this.dtw = data.dtw || null;
    this.temperature = data.temperature || null;
    this.ph = data.ph || null;
    // Unit of measure for specific conductance because it can change from ms/cm to us/cm based on the instrument
    this.specificConductanceUnit = data.specific_conductance_unit || null;
    this.specificConductance = data.specific_conductance || null;
    this.orp = data.orp || null;
    this.doMeasurement = data.do_measurement || null;
    this.turbidity = data.turbidity || null;
  }

  setMeasurementDate(measurementDate) {
    if (!measurementDate) {
      this.measurementDate = null;
      return;
    }
    // Formatting the incoming date if it is a date type
    if (measurementDate instanceof Date) {
      const formattedDate = measurementDate.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      this.measurementDate = formattedDate;
    } else {
      this.measurementDate = measurementDate;
    }
  }

  setMeasurementTime(measurementTime) {
    if (!measurementTime) {
      this.measurementTime = null;
      return;
    }
    this.measurementTime = measurementTime;
  }

  setExtractedVolume(extractedVolume) {
    if (!extractedVolume) {
      this.extractedVolume = null;
      return;
    }
    this.extractedVolume = extractedVolume;
  }

  setExtractedVolumeUnit(extractedVolumeUnit) {
    if (!extractedVolumeUnit) {
      this.extractedVolumeUnit = null;
      return;
    }
    this.extractedVolumeUnit = extractedVolumeUnit;
  }

  setDtw(dtw) {
    if (!dtw) {
      this.dtw = null;
      return;
    }
    this.dtw = dtw;
  }

  setTemperature(temperature) {
    if (!temperature) {
      this.temperature = null;
      return;
    }
    this.temperature = temperature;
  }

  setPh(ph) {
    if (!ph) {
      this.ph = null;
      return;
    }
    this.ph = ph;
  }

  setSpecificConductanceUnit(specificConductanceUnit) {
    if (!specificConductanceUnit) {
      this.specificConductanceUnit = null;
      return;
    }
    this.specificConductanceUnit = specificConductanceUnit;
  }

  setSpecificConductance(specificConductance) {
    if (!specificConductance) {
      this.specificConductance = null;
      return;
    }
    this.specificConductance = specificConductance;
  }

  setOrp(orp) {
    if (!orp) {
      this.orp = null;
      return;
    }
    this.orp = orp;
  }

  // Apparents "do" is not a valid parameter to pass, so we call it doMeasurement.
  setDoMeasurement(doMeasurement) {
    if (!doMeasurement) {
      this.doMeasurement = null;
      return;
    }
    this.doMeasurement = doMeasurement;
  }

  setTurbidity(turbidity) {
    if (!turbidity) {
      this.turbidity = null;
      return;
    }
    this.turbidity = turbidity;
  }

  // Sorting the field measurements by sort order
  setSortOrder(sortOrder) {
    if (!sortOrder) {
      this.sortOrder = null;
      return;
    }
    this.sortOrder = sortOrder;
  }

  // All data that will be sent for field measurements, is sent all at the same time, similar tot the WaterCalibrationLogs patch information. Also upon receipt of a patch, each field measurement is updated at the same time as well.
  updateFromPatch(patch) {
    this.measurementDate = patch.measurement_date;
    this.measurementTime = patch.measurement_time;
    this.extractedVolume = patch.extracted_volume;
    this.extractedVolumeUnit = patch.extracted_volume_unit;
    this.dtw = patch.dtw;
    this.temperature = patch.temperature;
    this.ph = patch.ph;
    this.specificConductanceUnit = patch.specific_conductance_unit;
    this.specificConductance = patch.specific_conductance;
    this.orp = patch.orp;
    this.doMeasurement = patch.do_measurement;
    this.turbidity = patch.turbidity;
    this.sortOrder = patch.sort_order;
  }

  toPatch(samplingEventWellId, action) {
    const patch = {
      patch_type: "sampling_event_field_measurements",
      action: action,
      sampling_event_well_id: samplingEventWellId,
      id: this.rowId,
      sort_order: this.sortOrder,
      well_id: this.wellId,
      measurement_date: this.measurementDate,
      measurement_time: this.measurementTime,
      extracted_volume: this.extractedVolume,
      extracted_volume_unit: this.extractedVolumeUnit,
      dtw: this.dtw,
      temperature: this.temperature,
      ph: this.ph,
      specific_conductance_unit: this.specificConductanceUnit,
      specific_conductance: this.specificConductance,
      orp: this.orp,
      do_measurement: this.doMeasurement,
      turbidity: this.turbidity,
    };
    return patch;
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.rowId,
      sort_order: this.sortOrder,
      well_id: this.wellId,
      measurement_date: this.measurementDate,
      measurement_time: this.measurementTime,
      extracted_volume: this.extractedVolume,
      dtw: this.dtw,
      temperature: this.temperature,
      ph: this.ph,
      specific_conductance_unit: this.specific_conductance_unit,
      specific_conductance: this.specificConductance,
      orp: this.orp,
      do_measurement: this.doMeasurement,
      turbidity: this.turbidity,
    };
  }
}

// Wells class
export class Wells {
  constructor(data = {}) {
    // Basic well information
    this.id = data.id || uuidv4();
    this.samplingEventId = data.sampling_event_id || "";
    this.wellId = data.well_id || "";
    this.siteName = data.site_name || "";
    this.status = data.status || "";
    this.wellPurpose = data.well_purpose || "";
    this.zone = data.zone || "";

    // Physical well characteristics
    this.installationDate = data.installation_date || "";
    this.casingDiameter = data.casing_diameter || "";
    this.casingDiameterUnit = data.casing_diameter_unit || "";
    this.casingType = data.casing_type || "";
    this.screenSlotSize = data.screen_slot_size || "";
    this.screenSlotSizeUnit = data.screen_slot_size_unit || "";
    this.screenType = data.screen_type || "";
    this.screenLength = data.screen_length || "";
    this.screenLengthUnit = data.screen_length_unit || "";
    this.sumpLength = data.sump_length || "";
    this.sumpLengthUnit = data.sump_length_unit || "";
    this.completedTopOfScreenDepth = data.completed_top_of_screen_depth || "";
    this.completedTopOfScreenDepthUnit =
      data.completed_top_of_screen_depth_unit || "";
    this.completedTopOfScreenDepthReference =
      data.completed_top_of_screen_depth_reference || "";
    this.completedBaseOfScreenDepth = data.completed_base_of_screen_depth || "";
    this.completedBaseOfScreenDepthUnit =
      data.completed_base_of_screen_depth_unit || "";
    this.completedBaseOfScreenDepthReference =
      data.completed_base_of_screen_depth_reference || "";
    this.totalDepth = data.total_depth || "";
    this.totalDepthUnit = data.total_depth_unit || "";
    this.stickup = data.stickup || "";
    this.stickupUnit = data.stickup_unit || "";
    this.topOfCasingElevation = data.top_of_casing_elevation || "";
    this.topOfCasingElevationUnit = data.top_of_casing_elevation_unit || "";
    this.topOfCasingElevationReference =
      data.top_of_casing_elevation_reference || "";
    this.groundSurfaceElevation = data.ground_surface_elevation || "";
    this.groundSurfaceElevationUnit = data.ground_surface_elevation_unit || "";
    this.groundSurfaceElevationReference =
      data.ground_surface_elevation_reference || "";
    this.xCoordinate = data.x_coordinate || "";
    this.yCoordinate = data.y_coordinate || "";
    this.longitude = data.longitude || "";
    this.latitude = data.latitude || "";

    // Well inspection data
    this.facilityMapEasyToFind = data.facility_map_easy_to_find || "";
    this.aboveGroundFlushMount = data.above_ground_flush_mount || "";
    this.freeOfDamage = data.free_of_damage || "";
    this.legibleWellId = data.legible_well_id || "";
    this.casingProtected = data.casing_protected || "";
    this.weedsTrash = data.weeds_trash || "";
    this.casingSecure = data.casing_secure || "";
    this.surfacePadCracked = data.surface_pad_cracked || "";
    this.surfacePadProneWaterPonding =
      data.surface_pad_prone_water_ponding || "";
    this.wellSecureLocked = data.well_secure_locked || "";
    this.surveyGaugingMarks = data.survey_gauging_marks || "";
    this.inspectionComments = data.inspection_comments || "";
    this.inspectionCompleted = data.inspection_completed || false;
    this.inspectionCompletedBy = data.inspection_completed_by || "";
    this.inspectionCompletedDate = data.inspection_completed_date || "";

    // LNAPL Data Inspection
    this.lnaplPresentInspection = data.lnapl_present_inspection || false;
    this.lnaplThicknessInspection = data.lnapl_thickness_inspection || "";
    this.lnaplOdorInspection = data.lnapl_odor_inspection || "";
    this.lnaplColorInspection = data.lnapl_color_inspection || "";
    this.lnaplOtherInspection = data.lnapl_other_inspection || "";

    // DNAPL Data Inspection
    this.dnaplPresentInspection = data.dnapl_present_inspection || false;
    this.dnaplThicknessInspection = data.dnapl_thickness_inspection || "";
    this.dnaplOdorInspection = data.dnapl_odor_inspection || "";
    this.dnaplColorInspection = data.dnapl_color_inspection || "";
    this.dnaplOtherInspection = data.dnapl_other_inspection || "";

    // Ring Gauge Inspection Data
    this.measuredTotalDepth = data.measured_total_depth || "";
    this.measuredTotalDepthNm = data.measured_total_depth_nm || false;
    this.measuredTotalDepthNmReason = data.measured_total_depth_nm_reason || "";
    this.measuredDepthToWater = data.measured_depth_to_water || "";
    this.measuredDepthToWaterNm = data.measured_depth_to_water_nm || false;
    this.measuredDepthToWaterNmReason =
      data.measured_depth_to_water_nm_reason || "";
    this.inspectionFeaturesDepths = data.inspection_features_depths || "";
    this.bottomConditions = data.bottom_conditions || "";

    // Sampling Data
    this.weatherConditions = data.weather_conditions || "";
    this.precipitation = data.precipitation || false;
    this.samplingStartDate = data.sampling_start_date || "";
    this.samplingStartTime = data.sampling_start_time || "";
    this.samplingEndDate = data.sampling_end_date || "";
    this.samplingEndTime = data.sampling_end_time || "";
    this.sampleTargetDepth = data.sample_target_depth || "";
    this.sampleTargetDepthNm = data.sample_target_depth_nm || false;
    this.extractionRate = data.extraction_rate || "";
    this.extractionRateNm = data.extraction_rate_nm || false;
    this.volumePurged = data.volume_purged || "";
    this.lowFlow = data.low_flow || false;
    this.purgeThreeWvs = data.purge_three_wvs || false;
    this.purgeVolumeIfPurgeThreeWvs =
      data.purge_volume_if_purge_three_wvs || "";
    this.purgeDevice = data.purge_device || "";
    this.purgeDeviceOther = data.purge_device_other || "";
    this.samplingDevice = data.sampling_device || "";
    this.samplingDeviceOther = data.sampling_device_other || "";
    this.sampleFiltered = data.sample_filtered || false;
    this.dedicatedEquipment = data.dedicated_equipment || false;
    this.samplingComments = data.sampling_comments || "";
    this.samplingBottomConditions = data.sampling_bottom_conditions || "";
    this.measuredDepthToWaterSampling =
      data.measured_depth_to_water_sampling || "";
    this.measuredTotalDepthSampling = data.measured_total_depth_sampling || "";

    // LNAPL Data Sampling
    this.lnaplPresent = data.lnapl_present || false;
    this.lnaplThickness = data.lnapl_thickness || "";
    this.lnaplOdor = data.lnapl_odor || "";
    this.lnaplColor = data.lnapl_color || "";
    this.lnaplOther = data.lnapl_other || "";

    // DNAPL Data Sampling
    this.dnaplPresent = data.dnapl_present || false;
    this.dnaplThickness = data.dnapl_thickness || "";
    this.dnaplOdor = data.dnapl_odor || "";
    this.dnaplColor = data.dnapl_color || "";
    this.dnaplOther = data.dnapl_other || "";

    // Sampling Information
    this.unableToCollectSample = data.unable_to_collect_sample || false;
    this.unableToCollectSampleReason =
      data.unable_to_collect_sample_reason || "";
    this.sampleTime = data.sample_time || "";
    this.sampleDate = data.sample_date || "";
    // For the id of the well sample
    this.wellSampleId = data.well_sample_id || "";
    this.qcSamples = data.qc_samples || "";
    this.sampleAppearance = data.sample_appearance || "";
    this.sampleColor = data.sample_color || "";
    this.sampleOdor = data.sample_odor || "";
    this.sampleComments = data.sample_comments || "";
    this.samplingCompleted = data.sampling_completed || false;
    this.samplingCompletedDate = data.sampling_completed_date || "";
    // QC Sampling Signature Section
    this.qcSamplingSignature = data.qc_sampling_signature
      ? "data:image/png;base64," + data.qc_sampling_signature
      : "";
    this.qcSamplingSignatureDate = data.qc_sampling_signature_date || "";
    this.qcSamplingSignatureTime = data.qc_sampling_signature_time || "";
    this.qcSamplingSignatureEmployeeName =
      data.qc_sampling_signature_employee_name || "";
    this.qcSamplingSignatureEmployeeId =
      data.qc_sampling_signature_employee_id || "";
    // Field Measurements - using the new field_measurements key from backend
    this.fieldMeasurements =
      data.field_measurements && Array.isArray(data.field_measurements)
        ? data.field_measurements.map(
            (measurement) => new FieldMeasurements(measurement)
          )
        : [];

    // Employee Signatures - using the new employee_signatures key from backend
    this.employeeSignatures =
      data.employee_signatures && Array.isArray(data.employee_signatures)
        ? data.employee_signatures.map(
            (signature) => new SamplingEmployeesSignature(signature)
          )
        : [];
  }

  setFacilityMapEasyToFind(facilityMapEasyToFind) {
    if (!facilityMapEasyToFind) {
      this.facilityMapEasyToFind = "";
      return;
    }
    this.facilityMapEasyToFind = facilityMapEasyToFind;
  }

  setAboveGroundFlushMount(aboveGroundFlushMount) {
    if (!aboveGroundFlushMount) {
      this.aboveGroundFlushMount = "";
      return;
    }
    this.aboveGroundFlushMount = aboveGroundFlushMount;
  }

  setFreeOfDamage(freeOfDamage) {
    if (!freeOfDamage) {
      this.freeOfDamage = "";
      return;
    }
    this.freeOfDamage = freeOfDamage;
  }

  setLegibleWellId(legibleWellId) {
    if (!legibleWellId) {
      this.legibleWellId = "";
      return;
    }
    this.legibleWellId = legibleWellId;
  }

  setCasingProtected(casingProtected) {
    if (!casingProtected) {
      this.casingProtected = "";
      return;
    }
    this.casingProtected = casingProtected;
  }

  setWeedsTrash(weedsTrash) {
    if (!weedsTrash) {
      this.weedsTrash = "";
      return;
    }
    this.weedsTrash = weedsTrash;
  }

  setCasingSecure(casingSecure) {
    if (!casingSecure) {
      this.casingSecure = "";
      return;
    }
    this.casingSecure = casingSecure;
  }

  setSurfacePadCracked(surfacePadCracked) {
    if (!surfacePadCracked) {
      this.surfacePadCracked = "";
      return;
    }
    this.surfacePadCracked = surfacePadCracked;
  }

  setSurfacePadProneWaterPonding(surfacePadProneWaterPonding) {
    if (!surfacePadProneWaterPonding) {
      // see comment below.
      this.surfacePadProneWaterPonding = "";
      return;
    }
    this.surfacePadProneWaterPonding = surfacePadProneWaterPonding;
  }

  setWellSecureLocked(wellSecureLocked) {
    if (!wellSecureLocked) {
      this.wellSecureLocked = "";
      return;
    }
    this.wellSecureLocked = wellSecureLocked;
  }

  setSurveyGaugingMarks(surveyGaugingMarks) {
    if (!surveyGaugingMarks) {
      this.surveyGaugingMarks = "";
      return;
    }
    this.surveyGaugingMarks = surveyGaugingMarks;
  }

  setInspectionComments(inspectionComments) {
    if (!inspectionComments) {
      this.inspectionComments = "";
      return;
    }
    this.inspectionComments = inspectionComments;
  }

  setInspectionCompleted(inspectionCompleted) {
    if (!inspectionCompleted) {
      this.inspectionCompleted = false;
      return;
    }
    this.inspectionCompleted = inspectionCompleted;
  }

  setInspectionCompletedBy(inspectionCompletedBy) {
    if (!inspectionCompletedBy) {
      this.inspectionCompletedBy = "";
      return;
    }
    this.inspectionCompletedBy = inspectionCompletedBy;
  }

  setInspectionCompletedDate(inspectionCompletedDate) {
    if (!inspectionCompletedDate) {
      this.inspectionCompletedDate = "";
      return;
    }
    // Formatting the incoming date if it is a date type
    if (inspectionCompletedDate instanceof Date) {
      const formattedDate = inspectionCompletedDate.toLocaleDateString(
        "en-US",
        {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        }
      );
      this.inspectionCompletedDate = formattedDate;
    } else {
      this.inspectionCompletedDate = inspectionCompletedDate;
    }
  }

  // NAPL Inspection Section
  setLnaplPresentInspection(lnaplPresentInspection) {
    if (!lnaplPresentInspection) {
      this.lnaplPresentInspection = false;
      return;
    }
    this.lnaplPresentInspection = lnaplPresentInspection;
  }

  setLnaplThicknessInspection(lnaplThicknessInspection) {
    if (!lnaplThicknessInspection) {
      this.lnaplThicknessInspection = null;
      return;
    }
    this.lnaplThicknessInspection = lnaplThicknessInspection;
  }

  setLnaplOdorInspection(lnaplOdorInspection) {
    if (!lnaplOdorInspection) {
      this.lnaplOdorInspection = "";
      return;
    }
    this.lnaplOdorInspection = lnaplOdorInspection;
  }

  setLnaplColorInspection(lnaplColorInspection) {
    if (!lnaplColorInspection) {
      this.lnaplColorInspection = "";
      return;
    }
    this.lnaplColorInspection = lnaplColorInspection;
  }

  setLnaplOtherInspection(lnaplOtherInspection) {
    if (!lnaplOtherInspection) {
      this.lnaplOtherInspection = "";
      return;
    }
    this.lnaplOtherInspection = lnaplOtherInspection;
  }

  // DNAPL Inspection Section
  setDnaplPresentInspection(dnaplPresentInspection) {
    if (!dnaplPresentInspection) {
      this.dnaplPresentInspection = false;
      return;
    }
    this.dnaplPresentInspection = dnaplPresentInspection;
  }

  setDnaplThicknessInspection(dnaplThicknessInspection) {
    if (!dnaplThicknessInspection) {
      this.dnaplThicknessInspection = null;
      return;
    }
    this.dnaplThicknessInspection = dnaplThicknessInspection;
  }

  setDnaplOdorInspection(dnaplOdorInspection) {
    if (!dnaplOdorInspection) {
      this.dnaplOdorInspection = "";
      return;
    }
    this.dnaplOdorInspection = dnaplOdorInspection;
  }

  setDnaplColorInspection(dnaplColorInspection) {
    if (!dnaplColorInspection) {
      this.dnaplColorInspection = "";
      return;
    }
    this.dnaplColorInspection = dnaplColorInspection;
  }

  setDnaplOtherInspection(dnaplOtherInspection) {
    if (!dnaplOtherInspection) {
      this.dnaplOtherInspection = "";
      return;
    }
    this.dnaplOtherInspection = dnaplOtherInspection;
  }

  // Ring Gauge Update Section
  setMeasuredTotalDepth(measuredTotalDepth) {
    if (!measuredTotalDepth) {
      this.measuredTotalDepth = null;
      return;
    }
    this.measuredTotalDepth = measuredTotalDepth;
  }

  setMeasuredTotalDepthSampling(measuredTotalDepthSampling) {
    if (!measuredTotalDepthSampling) {
      this.measuredTotalDepthSampling = null;
      return;
    }
    this.measuredTotalDepthSampling = measuredTotalDepthSampling;
  }

  setMeasuredTotalDepthNm(measuredTotalDepthNm) {
    if (!measuredTotalDepthNm) {
      this.measuredTotalDepthNm = false;
      return;
    }
    this.measuredTotalDepthNm = measuredTotalDepthNm;
  }

  setMeasuredTotalDepthNmReason(measuredTotalDepthNmReason) {
    if (!measuredTotalDepthNmReason) {
      this.measuredTotalDepthNmReason = "";
      return;
    }
    this.measuredTotalDepthNmReason = measuredTotalDepthNmReason;
  }

  setMeasuredDepthToWater(measuredDepthToWater) {
    if (!measuredDepthToWater) {
      this.measuredDepthToWater = null;
      return;
    }
    this.measuredDepthToWater = measuredDepthToWater;
  }

  setMeasuredDepthToWaterSampling(measuredDepthToWaterSampling) {
    if (!measuredDepthToWaterSampling) {
      this.measuredDepthToWaterSampling = null;
      return;
    }
    this.measuredDepthToWaterSampling = measuredDepthToWaterSampling;
  }

  setMeasuredDepthToWaterNm(measuredDepthToWaterNm) {
    if (!measuredDepthToWaterNm) {
      this.measuredDepthToWaterNm = false;
      return;
    }
    this.measuredDepthToWaterNm = measuredDepthToWaterNm;
  }

  setMeasuredDepthToWaterNmReason(measuredDepthToWaterNmReason) {
    if (!measuredDepthToWaterNmReason) {
      this.measuredDepthToWaterNmReason = "";
      return;
    }
    this.measuredDepthToWaterNmReason = measuredDepthToWaterNmReason;
  }

  setBottomConditions(bottomConditions) {
    if (!bottomConditions) {
      this.bottomConditions = "";
      return;
    }
    this.bottomConditions = bottomConditions;
  }

  setInspectionFeaturesDepths(inspectionFeaturesDepths) {
    if (!inspectionFeaturesDepths) {
      this.inspectionFeaturesDepths = "";
      return;
    }
    this.inspectionFeaturesDepths = inspectionFeaturesDepths;
  }

  // Sampling Data Section
  setWeatherConditions(weatherConditions) {
    if (!weatherConditions) {
      this.weatherConditions = "";
      return;
    }
    this.weatherConditions = weatherConditions;
  }

  setSamplingStartDate(samplingStartDate) {
    if (!samplingStartDate) {
      this.samplingStartDate = "";
      return;
    }
    // Formatting the incoming date if it is a date type
    if (samplingStartDate instanceof Date) {
      const formattedDate = samplingStartDate.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      this.samplingStartDate = formattedDate;
    } else {
      this.samplingStartDate = samplingStartDate;
    }
  }

  setSamplingStartTime(samplingStartTime) {
    if (!samplingStartTime) {
      this.samplingStartTime = "";
      return;
    }
    this.samplingStartTime = samplingStartTime;
  }

  setSamplingEndDate(samplingEndDate) {
    if (!samplingEndDate) {
      this.samplingEndDate = "";
      return;
    }
    // Formatting the incoming date if it is a date type
    if (samplingEndDate instanceof Date) {
      const formattedDate = samplingEndDate.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      this.samplingEndDate = formattedDate;
    } else {
      this.samplingEndDate = samplingEndDate;
    }
  }

  setSamplingEndTime(samplingEndTime) {
    if (!samplingEndTime) {
      this.samplingEndTime = "";
      return;
    }
    this.samplingEndTime = samplingEndTime;
  }

  setSampleTargetDepth(sampleTargetDepth) {
    if (!sampleTargetDepth) {
      this.sampleTargetDepth = "";
      return;
    }
    this.sampleTargetDepth = sampleTargetDepth;
  }

  setSampleTargetDepthNm(sampleTargetDepthNm) {
    if (!sampleTargetDepthNm) {
      this.sampleTargetDepthNm = false;
      return;
    }
    this.sampleTargetDepthNm = sampleTargetDepthNm;
  }

  setExtractionRate(extractionRate) {
    if (!extractionRate) {
      this.extractionRate = "";
      return;
    }
    this.extractionRate = extractionRate;
  }

  setExtractionRateNm(extractionRateNm) {
    if (!extractionRateNm) {
      this.extractionRateNm = false;
      return;
    }
    this.extractionRateNm = extractionRateNm;
  }

  setVolumePurged(volumePurged) {
    if (!volumePurged) {
      this.volumePurged = "";
      return;
    }
    this.volumePurged = volumePurged;
  }

  setLowFlow(lowFlow) {
    if (!lowFlow) {
      this.lowFlow = false;
      return;
    }
    this.lowFlow = lowFlow;
  }

  setPurgeThreeWvs(purgeThreeWvs) {
    if (!purgeThreeWvs) {
      this.purgeThreeWvs = false;
      return;
    }
    this.purgeThreeWvs = purgeThreeWvs;
  }

  setPurgeVolumeIfPurgeThreeWvs(purgeVolumeIfPurgeThreeWvs) {
    if (!purgeVolumeIfPurgeThreeWvs) {
      this.purgeVolumeIfPurgeThreeWvs = null;
      return;
    }
    this.purgeVolumeIfPurgeThreeWvs = purgeVolumeIfPurgeThreeWvs;
  }

  setPurgeDevice(purgeDevice) {
    if (!purgeDevice) {
      this.purgeDevice = "";
      return;
    }
    this.purgeDevice = purgeDevice;
  }

  setPurgeDeviceOther(purgeDeviceOther) {
    if (!purgeDeviceOther) {
      this.purgeDeviceOther = "";
      return;
    }
    this.purgeDeviceOther = purgeDeviceOther;
  }

  setSamplingDevice(samplingDevice) {
    if (!samplingDevice) {
      this.samplingDevice = "";
      return;
    }
    this.samplingDevice = samplingDevice;
  }

  setSamplingDeviceOther(samplingDeviceOther) {
    if (!samplingDeviceOther) {
      this.samplingDeviceOther = "";
      return;
    }
    this.samplingDeviceOther = samplingDeviceOther;
  }

  setSampleFiltered(sampleFiltered) {
    if (!sampleFiltered) {
      this.sampleFiltered = false;
      return;
    }
    this.sampleFiltered = sampleFiltered;
  }

  setDedicatedEquipment(dedicatedEquipment) {
    if (!dedicatedEquipment) {
      this.dedicatedEquipment = false;
      return;
    }
    this.dedicatedEquipment = dedicatedEquipment;
  }

  setSamplingComments(samplingComments) {
    if (!samplingComments) {
      this.samplingComments = "";
      return;
    }
    this.samplingComments = samplingComments;
  }

  setSamplingBottomConditions(samplingBottomConditions) {
    if (!samplingBottomConditions) {
      this.samplingBottomConditions = "";
      return;
    }
    this.samplingBottomConditions = samplingBottomConditions;
  }

  // LNAPL Data Section
  setLnaplPresent(lnaplPresent) {
    if (!lnaplPresent) {
      this.lnaplPresent = false;
      return;
    }
    this.lnaplPresent = lnaplPresent;
  }

  setLnaplThickness(lnaplThickness) {
    if (!lnaplThickness) {
      this.lnaplThickness = null;
      return;
    }
    this.lnaplThickness = lnaplThickness;
  }

  setLnaplOdor(lnaplOdor) {
    if (!lnaplOdor) {
      this.lnaplOdor = "";
      return;
    }
    this.lnaplOdor = lnaplOdor;
  }

  setLnaplColor(lnaplColor) {
    if (!lnaplColor) {
      this.lnaplColor = "";
      return;
    }
    this.lnaplColor = lnaplColor;
  }

  setLnaplOther(lnaplOther) {
    if (!lnaplOther) {
      this.lnaplOther = "";
      return;
    }
    this.lnaplOther = lnaplOther;
  }

  // DNAPL Data Section
  setDnaplPresent(dnaplPresent) {
    if (!dnaplPresent) {
      this.dnaplPresent = false;
      return;
    }
    this.dnaplPresent = dnaplPresent;
  }

  setDnaplThickness(dnaplThickness) {
    if (!dnaplThickness) {
      this.dnaplThickness = null;
      return;
    }
    this.dnaplThickness = dnaplThickness;
  }

  setDnaplOdor(dnaplOdor) {
    if (!dnaplOdor) {
      this.dnaplOdor = "";
      return;
    }
    this.dnaplOdor = dnaplOdor;
  }

  setDnaplColor(dnaplColor) {
    if (!dnaplColor) {
      this.dnaplColor = "";
      return;
    }
    this.dnaplColor = dnaplColor;
  }

  setDnaplOther(dnaplOther) {
    if (!dnaplOther) {
      this.dnaplOther = "";
      return;
    }
    this.dnaplOther = dnaplOther;
  }

  // Sampling Information Section
  setUnableToCollectSample(unableToCollectSample) {
    if (!unableToCollectSample) {
      this.unableToCollectSample = false;
      this.unableToCollectSampleReason = "";
      return;
    }
    this.unableToCollectSample = unableToCollectSample;
  }

  setUnableToCollectSampleReason(unableToCollectSampleReason) {
    if (!unableToCollectSampleReason) {
      this.unableToCollectSampleReason = "";
      return;
    }
    this.unableToCollectSampleReason = unableToCollectSampleReason;
  }

  setSampleTime(sampleTime) {
    if (!sampleTime) {
      this.sampleTime = "";
      return;
    }
    this.sampleTime = sampleTime;
  }

  setSampleDate(sampleDate) {
    if (!sampleDate) {
      this.sampleDate = "";
      return;
    }
    // Formatting the incoming date if it is a date type
    if (sampleDate instanceof Date) {
      const formattedDate = sampleDate.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      this.sampleDate = formattedDate;
    } else {
      this.sampleDate = sampleDate;
    }
  }

  setQcSamples(qcSamples) {
    if (!qcSamples) {
      this.qcSamples = "";
      return;
    }
    this.qcSamples = qcSamples;
  }

  setSampleAppearance(sampleAppearance) {
    if (!sampleAppearance) {
      this.sampleAppearance = "";
      return;
    }
    this.sampleAppearance = sampleAppearance;
  }

  setSampleColor(sampleColor) {
    if (!sampleColor) {
      this.sampleColor = "";
      return;
    }
    this.sampleColor = sampleColor;
  }

  setSampleOdor(sampleOdor) {
    if (!sampleOdor) {
      this.sampleOdor = "";
      return;
    }
    this.sampleOdor = sampleOdor;
  }

  setSampleComments(sampleComments) {
    if (!sampleComments) {
      this.sampleComments = "";
      return;
    }
    this.sampleComments = sampleComments;
  }

  // Field Measurements Section
  addBlankFieldMeasurement() {
    const newFieldMeasurement = new FieldMeasurements({
      well_id: this.wellId,
      sampling_event_well_id: this.id,
      sampling_event_id: this.samplingEventId,
      sort_order: this.fieldMeasurements.length,
    });
    this.fieldMeasurements.push(newFieldMeasurement);
    return newFieldMeasurement;
  }

  // Resetting the sort order of the field measurements if a user drags and drops a field measurement
  resetSortOrder(newFieldMeasurements) {
    newFieldMeasurements.forEach((newFieldMeasurement, index) => {
      this.fieldMeasurements
        .find(
          (fieldMeasurement) =>
            fieldMeasurement.rowId === newFieldMeasurement.rowId
        )
        .setSortOrder(index);
    });
    // Sorting the field measurements by sort order
    this.fieldMeasurements.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  removeFieldMeasurement(rowId) {
    this.fieldMeasurements = this.fieldMeasurements.filter(
      (fieldMeasurement) => fieldMeasurement.rowId !== rowId
    );
    // Updating the sort order of the field measurements after the new field measurement, if the index is greater than the index of the new field measurement, then decrement the sort order by 1
    this.fieldMeasurements.forEach((fieldMeasurement) => {
      const index = this.fieldMeasurements.indexOf(fieldMeasurement);
      if (fieldMeasurement.sortOrder !== index) {
        fieldMeasurement.sortOrder = index;
      }
    });
  }

  sortFieldMeasurements() {
    this.fieldMeasurements.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  // Employee Signatures Section
  addBlankEmployeeSignature() {
    const newSignature = new SamplingEmployeesSignature({
      sampling_event_well_id: this.id,
    });
    this.employeeSignatures.push(newSignature);
    return newSignature;
  }

  removeEmployeeSignature(rowId) {
    this.employeeSignatures = this.employeeSignatures.filter(
      (signature) => signature.rowId !== rowId
    );
  }

  updateEmployeeSignatureEmployee(rowId, employee) {
    const signature = this.employeeSignatures.find(
      (signature) => signature.rowId === rowId
    );
    if (signature) {
      signature.setSelectedEmployee(employee);
    }
  }

  updateEmployeeSignatureSignature(rowId, signature) {
    const employeeSignature = this.employeeSignatures.find(
      (employeeSignature) => employeeSignature.rowId === rowId
    );
    if (employeeSignature) {
      employeeSignature.setSignature(signature);
    }
  }

  setSamplingCompleted(value) {
    if (!value) {
      this.samplingCompleted = false;
      return;
    }
    this.samplingCompleted = value;
  }

  setSamplingCompletedDate(value) {
    if (!value) {
      this.samplingCompletedDate = "";
      return;
    }
    // Formatting the incoming date if it is a date type
    if (value instanceof Date) {
      const formattedDate = value.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      this.samplingCompletedDate = formattedDate;
    } else {
      this.samplingCompletedDate = value;
    }
  }

  setQcSamplingSignature(signature) {
    if (!signature) {
      this.qcSamplingSignature = "";
    } else {
      if (signature.startsWith("data:image/png;base64,")) {
        this.qcSamplingSignature = signature;
      } else {
        this.qcSamplingSignature = "data:image/png;base64," + signature;
      }
    }
  }

  setQcSamplingSignatureDate(date) {
    if (!date) {
      this.qcSamplingSignatureDate = "";
      return;
    }
    // Formatting the incoming date if it is a date type
    if (date instanceof Date) {
      const formattedDate = date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      this.qcSamplingSignatureDate = formattedDate;
    } else {
      this.qcSamplingSignatureDate = date;
    }
  }

  setQcSamplingSignatureTime(time) {
    if (!time) {
      this.qcSamplingSignatureTime = "";
      return;
    }
    this.qcSamplingSignatureTime = time;
  }

  setQcSamplingSignatureEmployeeName(employeeName) {
    if (!employeeName) {
      this.qcSamplingSignatureEmployeeName = "";
      return;
    }
    this.qcSamplingSignatureEmployeeName = employeeName;
  }

  setQcSamplingSignatureEmployeeId(employeeId) {
    if (!employeeId) {
      this.qcSamplingSignatureEmployeeId = "";
      return;
    }
    this.qcSamplingSignatureEmployeeId = employeeId;
  }

  /**
   * Validate the inspection section of the well and mark the inspection complete or not
   * @param {array} fieldsToValidate - The fields to validate, comes in an array of field name (corresponds to inspection fields in this class) and label we will return if the field is not filled out Ex: [{field: "measuredTotalDepth", label: "Measured Total Depth " }]
   * @returns {object} - An object with the errors that will be sent to display in a notifical modal.
   */
  validateInspectionAndMarkComplete(fieldsToValidate) {
    const errors = [];
    fieldsToValidate.forEach((item) => {
      // Check if total depth is required (not marked as "Not Measured")
      if (item.field === "measuredTotalDepth") {
        if (
          !this.measuredTotalDepthNm &&
          (!this[item.field] || this[item.field] === "")
        ) {
          errors.push({ field: item.field, label: item.label });
        }
      }
      // Check if depth to water is required (not marked as "Not Measured")
      else if (item.field === "measuredDepthToWater") {
        if (
          !this.measuredDepthToWaterNm &&
          (!this[item.field] || this[item.field] === "")
        ) {
          errors.push({ field: item.field, label: item.label });
        }
      }
      // If total depth is marked as "Not Measured", require a reason
      else if (item.field === "measuredTotalDepthNm") {
        if (
          this[item.field] === true &&
          (!this.measuredTotalDepthNmReason ||
            this.measuredTotalDepthNmReason.trim() === "")
        ) {
          errors.push({
            field: "measuredTotalDepthNmReason",
            label: "Reason for not measuring total depth",
          });
        }
      }
      // If depth to water is marked as "Not Measured", require a reason
      else if (item.field === "measuredDepthToWaterNm") {
        if (
          this[item.field] === true &&
          (!this.measuredDepthToWaterNmReason ||
            this.measuredDepthToWaterNmReason.trim() === "")
        ) {
          errors.push({
            field: "measuredDepthToWaterNmReason",
            label: "Reason for not measuring depth to water",
          });
        }
      } else if (!this[item.field] || this[item.field] === "") {
        errors.push({ field: item.field, label: item.label });
      }
    });
    return errors;
  }

  /**
   * Validate the sampling section of the well and mark the samplling complete or not
   * @param {array} fieldErrorArray - Array of error labels for each field that is required, basically a hash map of what to keep in our error arrays
   * @param {array} fieldMeasurementErrorArray - Array of error labels for each field that is required for the field measurements, basically a hash map of what to keep in our error arrays
   * @returns {object} - An object with the errors that will be sent to display in a notifical modal.
   */
  validateSamplingAndMarkComplete(fieldErrorArray, fieldMeasurementErrorArray) {
    const errors = [];
    // Unable to collect sample logic: if checked and reason present, bypass all other checks, since the well cant be check
    const unable = Boolean(this.unableToCollectSample);
    const unableReason = String(this.unableToCollectSampleReason || "").trim();
    if (unable && unableReason.length === 0) {
      errors.push({
        field: "unableToCollectSampleReason",
        label: fieldErrorArray.unableToCollectSampleReason.label,
      });
    }

    // If the user checked the unable to collect sample box, we can skip the following checks
    if (!unable) {
      // Check all the fields that are not conditional
      Object.keys(fieldErrorArray).forEach((field) => {
        // The fieldErrorArray should have a section for conditional or not, if not conditional check the sampling field.
        if (!fieldErrorArray[field].conditional) {
          if (!this[field] || String(this[field]).trim() === "") {
            errors.push({ field: field, label: fieldErrorArray[field].label });
          }
        }
      });
      // Bypassing Target Sample Depth and Extraction Rate if the N/A checkbox is checked
      if (!this.sampleTargetDepthNm) {
        if (String(this.sampleTargetDepth).trim() === "") {
          errors.push({
            field: "sampleTargetDepth",
            label: fieldErrorArray.sampleTargetDepth.label,
          });
        }
      }
      if (!this.extractionRateNm) {
        if (String(this.extractionRate).trim() === "") {
          errors.push({
            field: "extractionRate",
            label: fieldErrorArray.extractionRate.label,
          });
        }
      }

      // Check purge logic
      if (!this.lowFlow && !this.purgeThreeWvs) {
        errors.push({
          field: "samplingMethod",
          label: "Sampling Method (Low Flow or Purge 3 WVs)",
        });
      }
      if (
        this.purgeThreeWvs &&
        (!this.purgeVolumeIfPurgeThreeWvs ||
          String(this.purgeVolumeIfPurgeThreeWvs).trim() === "")
      ) {
        errors.push({
          field: "purgeVolumeIfPurgeThreeWvs",
          label: fieldErrorArray.purgeVolumeIfPurgeThreeWvs.label,
        });
      }
      if (
        this.volumePurged &&
        String(this.volumePurged).trim() !== "" &&
        (!this.purgeDevice || String(this.purgeDevice).trim() === "")
      ) {
        errors.push({
          field: "purgeDevice",
          label: fieldErrorArray.purgeDevice.label,
        });
      }

      // Check LNAPL fields if present
      if (this.lnaplPresent) {
        ["lnaplThickness", "lnaplOdor", "lnaplColor"].forEach((field) => {
          if (!this[field] || String(this[field]).trim() === "") {
            errors.push({ field, label: fieldErrorArray[field].label });
          }
        });
      }

      // Check DNAPL fields if present
      if (this.dnaplPresent) {
        ["dnaplThickness", "dnaplOdor", "dnaplColor"].forEach((field) => {
          if (!this[field] || String(this[field]).trim() === "") {
            errors.push({ field, label: fieldErrorArray[field].label });
          }
        });
      }
      // Check Field Measurements
      if (!this.fieldMeasurements || this.fieldMeasurements.length === 0) {
        errors.push({
          field: "fieldMeasurements",
          label: "Field Measurements (at least one row)",
        });
      } else {
        // Loop through each field measurement and check if the field is filled out, then send off to the error list with the appropriate message.
        this.fieldMeasurements.forEach((measurement, index) => {
          Object.keys(fieldMeasurementErrorArray).forEach((field) => {
            if (
              !measurement[field] ||
              String(measurement[field]).trim() === ""
            ) {
              errors.push({
                field: `fieldMeasurement_${index}_${field}`,
                label: `Field Measurement Row ${index + 1} - ${
                  fieldMeasurementErrorArray[field].label
                }
                `,
              });
            }
          });
        });
      }
    }
    // Check Signatures
    if (!this.employeeSignatures || this.employeeSignatures.length === 0) {
      errors.push({
        field: "employeeSignatures",
        label: "Sampler Signatures (at least one row)",
      });
    } else {
      this.employeeSignatures.forEach((signature, index) => {
        if (
          !signature.employeeId ||
          String(signature.employeeId).trim() === ""
        ) {
          errors.push({
            field: `signature_${index}_employee`,
            label: `Sampler Signature Row ${index + 1} - Employee`,
          });
        }
        if (!signature.signature || String(signature.signature).trim() === "") {
          errors.push({
            field: `signature_${index}_signature`,
            label: `Sampler Signature Row ${index + 1} - Signature`,
          });
        }
      });
    }
    return errors;
  }

  // Helper function to turn fields that camelcase to snakecase
  setSnakeCaseField(field) {
    return field.replace(/([A-Z])/g, "_$1").toLowerCase();
  }

  // Method for well inspection patchs since it is another tab with separate data
  // patchType is the type of patch to apply, such as "update", "add"
  // patchData is the data to apply to the well inspection, comes in an array like [{field: value}, {field, value}] where field matches the fields above and value is what needs to be updated on the server.
  baseWellPatch(action, patchData) {
    const patch = {
      patch_type: "sampling_event_wells_inspection",
      action: action,
      id: this.id,
      sampling_event_id: this.samplingEventId,
      well_id: this.wellId,
    };
    patchData.forEach((field) => {
      // console.log("field", field);
      // console.log("value", value);
      const snakeCaseField = this.setSnakeCaseField(field);
      if (snakeCaseField) {
        patch[snakeCaseField] = this[field];
      }
    });
    return patch;
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.id,
      sampling_event_id: this.samplingEventId,
      well_id: this.wellId,
      // Basic well information
      site_name: this.siteName,
      status: this.status,
      well_purpose: this.wellPurpose,
      zone: this.zone,
      // Physical well characteristics
      installation_date: this.installationDate,
      casing_diameter: this.casingDiameter,
      casing_diameter_unit: this.casingDiameterUnit,
      casing_type: this.casingType,
      screen_slot_size: this.screenSlotSize,
      screen_slot_size_unit: this.screenSlotSizeUnit,
      screen_type: this.screenType,
      screen_length: this.screenLength,
      screen_length_unit: this.screenLengthUnit,
      sump_length: this.sumpLength,
      sump_length_unit: this.sumpLengthUnit,
      completed_top_of_screen_depth: this.completedTopOfScreenDepth,
      completed_top_of_screen_depth_unit: this.completedTopOfScreenDepthUnit,
      completed_top_of_screen_depth_reference:
        this.completedTopOfScreenDepthReference,
      completed_base_of_screen_depth: this.completedBaseOfScreenDepth,
      completed_base_of_screen_depth_unit: this.completedBaseOfScreenDepthUnit,
      completed_base_of_screen_depth_reference:
        this.completedBaseOfScreenDepthReference,
      total_depth: this.totalDepth,
      total_depth_unit: this.totalDepthUnit,
      stickup: this.stickup,
      stickup_unit: this.stickupUnit,
      top_of_casing_elevation: this.topOfCasingElevation,
      top_of_casing_elevation_unit: this.topOfCasingElevationUnit,
      top_of_casing_elevation_reference: this.topOfCasingElevationReference,
      ground_surface_elevation: this.groundSurfaceElevation,
      ground_surface_elevation_unit: this.groundSurfaceElevationUnit,
      ground_surface_elevation_reference: this.groundSurfaceElevationReference,
      x_coordinate: this.xCoordinate,
      y_coordinate: this.yCoordinate,
      longitude: this.longitude,
      latitude: this.latitude,
      facility_map_easy_to_find: this.facilityMapEasyToFind,
      above_ground_flush_mount: this.aboveGroundFlushMount,
      free_of_damage: this.freeOfDamage,
      legible_well_id: this.legibleWellId,
      casing_protected: this.casingProtected,
      weeds_trash: this.weedsTrash,
      casing_secure: this.casingSecure,
      surface_pad_cracked: this.surfacePadCracked,
      surface_pad_prone_water_ponding: this.surfacePadProneWaterPonding,
      well_secure_locked: this.wellSecureLocked,
      survey_gauging_marks: this.surveyGaugingMarks,
      bottom_conditions: this.bottomConditions,
      inspection_comments: this.inspectionComments,
      inspection_completed: this.inspectionCompleted,
      inspection_completed_by: this.inspectionCompletedBy,
      inspection_completed_date: this.inspectionCompletedDate,
      // NAPL Inspection Section
      lnapl_present_inspection: this.lnaplPresentInspection,
      lnapl_thickness_inspection: this.lnaplThicknessInspection,
      lnapl_odor_inspection: this.lnaplOdorInspection,
      lnapl_color_inspection: this.lnaplColorInspection,
      lnapl_other_inspection: this.lnaplOtherInspection,
      dnapl_present_inspection: this.dnaplPresentInspection,
      dnapl_thickness_inspection: this.dnaplThicknessInspection,
      dnapl_odor_inspection: this.dnaplOdorInspection,
      dnapl_color_inspection: this.dnaplColorInspection,
      dnapl_other_inspection: this.dnaplOtherInspection,
      // Ring Gauge Inspection Data
      measured_total_depth: this.measuredTotalDepth,
      measured_total_depth_nm: this.measuredTotalDepthNm,
      measured_total_depth_nm_reason: this.measuredTotalDepthNmReason,
      measured_depth_to_water: this.measuredDepthToWater,
      measured_depth_to_water_nm: this.measuredDepthToWaterNm,
      measured_depth_to_water_nm_reason: this.measuredDepthToWaterNmReason,
      inspection_features_depths: this.inspectionFeaturesDepths,
      weather_conditions: this.weatherConditions,
      precipitation: this.precipitation,
      unable_to_collect_sample: this.unableToCollectSample,
      unable_to_collect_sample_reason: this.unableToCollectSampleReason,
      sampling_start_date: this.samplingStartDate,
      sampling_start_time: this.samplingStartTime,
      sampling_end_date: this.samplingEndDate,
      sampling_end_time: this.samplingEndTime,
      sample_target_depth: this.sampleTargetDepth,
      sample_target_depth_nm: this.sampleTargetDepthNm,
      extraction_rate: this.extractionRate,
      extraction_rate_nm: this.extractionRateNm,
      volume_purged: this.volumePurged,
      low_flow: this.lowFlow,
      purge_three_wvs: this.purgeThreeWvs,
      purge_volume_if_purge_three_wvs: this.purgeVolumeIfPurgeThreeWvs,
      purge_device: this.purgeDevice,
      purge_device_other: this.purgeDeviceOther,
      sampling_device: this.samplingDevice,
      sampling_device_other: this.samplingDeviceOther,
      sample_filtered: this.sampleFiltered,
      dedicated_equipment: this.dedicatedEquipment,
      sampling_comments: this.samplingComments,
      sampling_bottom_conditions: this.samplingBottomConditions,
      // Include sampling depth fields so clone()/toJson() preserves them
      measured_depth_to_water_sampling: this.measuredDepthToWaterSampling,
      measured_total_depth_sampling: this.measuredTotalDepthSampling,
      lnapl_present: this.lnaplPresent,
      lnapl_thickness: this.lnaplThickness,
      lnapl_odor: this.lnaplOdor,
      lnapl_color: this.lnaplColor,
      lnapl_other: this.lnaplOther,
      dnapl_present: this.dnaplPresent,
      dnapl_thickness: this.dnaplThickness,
      dnapl_odor: this.dnaplOdor,
      dnapl_color: this.dnaplColor,
      dnapl_other: this.dnaplOther,
      sample_time: this.sampleTime,
      sample_date: this.sampleDate,
      well_sample_id: this.wellSampleId,
      qc_samples: this.qcSamples,
      sample_appearance: this.sampleAppearance,
      sample_color: this.sampleColor,
      sample_odor: this.sampleOdor,
      sample_comments: this.sampleComments,
      sampling_completed: this.samplingCompleted,
      sampling_completed_date: this.samplingCompletedDate,
      qc_sampling_signature: this.qcSamplingSignature.replace(
        "data:image/png;base64,",
        ""
      ),
      qc_sampling_signature_date: this.qcSamplingSignatureDate,
      qc_sampling_signature_time: this.qcSamplingSignatureTime,
      qc_sampling_signature_employee_name: this.qcSamplingSignatureEmployeeName,
      qc_sampling_signature_employee_id: this.qcSamplingSignatureEmployeeId,
      field_measurements: this.fieldMeasurements.map((fieldMeasurement) =>
        fieldMeasurement.toJson()
      ),
      employee_signatures: this.employeeSignatures.map((signature) =>
        signature.toJson()
      ),
    };
  }
}
